import { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControlLabel,
  Switch,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Typography,
  Box,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  CircularProgress,
  Alert
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { useAuth } from '../../../AuthContext';
import { fetchData } from '../../../apiService';
import './AccountHeadModal.css';

const AccountHeadModal = ({
  open,
  onClose,
  modalAction,
  selectedItem,
  formData,
  setFormData,
  onSave,
  loading,
  accountHeads = [],
  balanceSheetHeads = [],
  mainHeads = []
}) => {
  const { companyId, token } = useAuth();
  const [errors, setErrors] = useState({});

  // Branch-wise opening balance states
  const [branches, setBranches] = useState([]);
  const [branchesLoading, setBranchesLoading] = useState(false);
  const [apiOpeningBalances, setApiOpeningBalances] = useState(null);

  const today = new Date().toISOString().split('T')[0];

  // Fetch branches when modal opens
  useEffect(() => {
    if (open && companyId && token) {
      fetchBranches();
    } else if (!open) {
      setBranches([]);
      setApiOpeningBalances(null);
    }
  }, [open, companyId, token]);

  useEffect(() => {
    if (selectedItem && modalAction !== 'create') {
      setApiOpeningBalances(selectedItem.OpeningBalances || []);
      setFormData({ ...selectedItem, OpeningBalances: [] });
    } else if (modalAction === 'create') {
      setApiOpeningBalances(null);
      setFormData(getDefaultFormData());
    }
  }, [selectedItem, modalAction]);

  const fetchBranches = async () => {
    setBranchesLoading(true);
    try {
      const response = await fetchData('BranchApi', `/${companyId}`, token);
      const data = response.response_data || response || [];
      setBranches(Array.isArray(data) ? data : [data]);
    } catch (error) {
      console.error('Error fetching branches:', error);
      setBranches([]);
    }
    setBranchesLoading(false);
  };

  // Merge branches with API opening balances once both are ready
  useEffect(() => {
    if (branches.length === 0) return;

    if (modalAction === 'create') {
      setFormData(prev => ({
        ...prev,
        OpeningBalances: branches.map(b => ({
          BranchId: b.Id,
          BranchName: b.Name || b.BranchName || '',
          OpeningBalance: 0,
          OpeningDate: today,
          IsPayableOrReceivable: false
        }))
      }));
    } else if ((modalAction === 'edit' || modalAction === 'view') && apiOpeningBalances !== null) {
      const merged = branches.map(b => {
        const found = apiOpeningBalances.find(r => r.BranchId === b.Id);
        return found
          ? { ...found, Id: found.Id || 0, BranchName: b.Name || b.BranchName || found.BranchName || '' }
          : {
              Id: 0,
              BranchId: b.Id,
              BranchName: b.Name || b.BranchName || '',
              OpeningBalance: 0,
              OpeningDate: today,
              IsPayableOrReceivable: false
            };
      });
      setFormData(prev => ({ ...prev, OpeningBalances: merged }));
    }
  }, [branches, modalAction, apiOpeningBalances]);

  const handleOpeningBalanceChange = (branchId, field, value) => {
    setFormData(prev => ({
      ...prev,
      OpeningBalances: (prev.OpeningBalances || []).map(row =>
        row.BranchId === branchId ? { ...row, [field]: value } : row
      )
    }));
  };

  const getDefaultFormData = () => {
    return {
      Name: '',
      BalanceSheetHeadId: '',
      Description: '',
      IsActive: true,
      MainHeadId: null,
      GSTRate: 0,
      SACCode: '',
      IsExpenseOrIncome: false,
      IsTrading: false,
      OpeningBalances: []
    };
  };

  // ── Duplicate detection ──────────────────────────────────────────────────
  // Duplicate = same Name + BalanceSheetHeadId + IsExpenseOrIncome
  // MainHeadId is NOT part of the duplicate key.
  const isDuplicate = useMemo(() => {
    if (modalAction === 'view') return false;

    const currentName = (formData.Name || formData.name || '').trim().toLowerCase();
    if (!currentName) return false;

    const currentBSH  = formData.BalanceSheetHeadId || formData.balanceSheetHeadId;
    const currentIsEI = Boolean(formData.IsExpenseOrIncome ?? formData.isExpenseOrIncome ?? false);

    // The id of the record being edited (skip self-comparison)
    const currentId = selectedItem?.MasterId || selectedItem?.Id || 0;

    return accountHeads.some(head => {
      // skip self when editing
      const headId = head.MasterId || head.Id || 0;
      if (modalAction === 'edit' && headId === currentId) return false;

      const headName = (head.Name || '').trim().toLowerCase();
      if (headName !== currentName) return false;

      // Check BalanceSheetHeadId if available
      if (currentBSH && head.BalanceSheetHeadId) {
        if (String(currentBSH) !== String(head.BalanceSheetHeadId)) return false;
      }

      // Check IsExpenseOrIncome flag
      const headIsEI = Boolean(head.IsExpenseOrIncome ?? false);
      return currentIsEI === headIsEI;
    });
  }, [
    formData.Name, formData.name,
    formData.BalanceSheetHeadId, formData.balanceSheetHeadId,
    formData.IsExpenseOrIncome, formData.isExpenseOrIncome,
    accountHeads, modalAction, selectedItem
  ]);

  // ── Validation ────────────────────────────────────────────────────────────
  const validateForm = () => {
    const newErrors = {};
    
    // Helper to get value from either lowercase or PascalCase field
    const getValue = (lowercase, pascalCase) => formData[lowercase] ?? formData[pascalCase];
    
    if (!(getValue('name', 'Name')?.toString().trim())) {
      newErrors.name = 'Account name is required';
    }
    
    if (!(getValue('balanceSheetHeadId', 'BalanceSheetHeadId')?.toString().trim())) {
      newErrors.balanceSheetHeadId = 'Balance Sheet is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handleSave = () => {
    if (isDuplicate) return; // extra guard
    if (validateForm()) {
      onSave();
    }
  };

  const getModalTitle = () => {
    const actionNames = {
      create: 'New',
      edit: 'Edit',
      view: 'View'
    };
    
    const actionName = actionNames[modalAction] || 'New';
    
    return {
      title: `${actionName} Account`,
      subtitle: `${modalAction === 'create' ? 'Add a new' : modalAction === 'edit' ? 'Update' : 'View'} account ${modalAction === 'create' ? 'to your chart of accounts' : 'details'}`
    };
  };


  const renderForm = () => (
    <div className="form-container">
      {/* Basic Information Section */}
      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1e3a8a', mb: 0.5, mt: 0.5, fontSize: '0.75rem' }}>
        Basic Information
      </Typography>
      
      <div className="form-row">
        <div className="form-field required-field">
          <TextField
            fullWidth
            label="Account Name"
            placeholder="Enter account name"
            value={formData.Name || formData.name || ''}
            onChange={(e) => handleInputChange('Name', e.target.value)}
            error={!!errors.name || isDuplicate}
            helperText={errors.name || (isDuplicate ? 'This account name already exists with the same Balance Sheet & Income/Expense type' : '')}
            disabled={modalAction === 'view'}
            required
          />
        </div>
      </div>
      
      {/* Account Setup Section */}
      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1e3a8a', mb: 0.5, mt: 1, fontSize: '0.75rem' }}>
        Account Setup
      </Typography>
      
      <div className="form-row">
        <div className="form-field required-field">
          <FormControl fullWidth error={!!errors.balanceSheetHeadId} required>
            <InputLabel>Balance Sheet</InputLabel>
            <Select
              value={Number(formData.BalanceSheetHeadId || formData.balanceSheetHeadId) || ''}
              onChange={(e) => handleInputChange('BalanceSheetHeadId', e.target.value)}
              disabled={modalAction === 'view'}
              label="Balance Sheet *"
            >
              <MenuItem value="">
                <em>Select Balance Sheet</em>
              </MenuItem>
              {balanceSheetHeads.map(head => (
                <MenuItem key={head.Id} value={head.Id}>
                  {head.Name}
                </MenuItem>
              ))}
            </Select>
            {errors.balanceSheetHeadId && (
              <Typography variant="caption" color="error" sx={{ ml: 0, mt: 0.5 }}>
                {errors.balanceSheetHeadId}
              </Typography>
            )}t   
          </FormControl>
        </div>
        <div className="form-field">
          <FormControl fullWidth>
            <InputLabel>Parent Account (Optional)</InputLabel>
            <Select
              value={Number(formData.MainHeadId || formData.mainHeadId) || ''}
              onChange={(e) => handleInputChange('MainHeadId', e.target.value || null)}
              disabled={modalAction === 'view'}
              label="Parent Account (Optional)"
            >
              <MenuItem value="">
                <em>Create as Main Account</em>
              </MenuItem>
              {mainHeads.map(head => (
                <MenuItem key={head.Id} value={head.Id}>
                  {head.Name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Typography 
            variant="caption" 
            sx={{ 
              color: '#6b7280', 
              fontSize: '0.75rem',
              display: 'block',
              mt: 0.5,
              fontStyle: 'italic'
            }}
          >
            Leave empty for main account, select parent for sub-account
          </Typography>
        </div>
      </div>

      {/* Duplicate entry warning */}
      {isDuplicate && (
        <Alert
          severity="error"
          sx={{ mt: 1, mb: 0.5, fontSize: '0.8rem', py: 0.5 }}
        >
          Duplicate Entry: An account head with the same <strong>Name</strong>, <strong>Balance Sheet</strong>, and <strong>Income/Expense</strong> type already exists.
        </Alert>
      )}

      {/* GST and SAC Information */}
      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1e3a8a', mb: 0.5, mt: 1, fontSize: '0.75rem' }}>
        GST & SAC Information
      </Typography>
      
      <div className="form-row">
        <div className="form-field">
          <TextField
            fullWidth
            label="GST Rate (%)"
            type="number"
            placeholder="Enter GST rate"
            value={formData.GSTRate || formData.GSTRate || 0}
            onChange={(e) => {
              const value = e.target.value;
              // Limit to 3 digits (0-100)
              if (value === '' || (parseFloat(value) >= 0 && parseFloat(value) <= 100)) {
                handleInputChange('GSTRate', parseFloat(value) || 0);
              }
            }}
            disabled={modalAction === 'view'}
            inputProps={{ min: 0, max: 100, step: 0.01, maxLength: 6 }}
            sx={{ maxWidth: '290px' }}
          />
        </div>
        <div className="form-field">
          <TextField
            fullWidth
            label="SAC Code"
            placeholder="Enter SAC code (max 10 chars)"
            value={formData.SACCode || formData.sacCode || ''}
            onChange={(e) => handleInputChange('SACCode', e.target.value)}
            disabled={modalAction === 'view'}
            inputProps={{ maxLength: 10 }}
          />
        </div>
      </div>

      {/* Account Settings */}
      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1e3a8a', mb: 0.5, mt: 1, fontSize: '0.75rem' }}>
        Account Settings
      </Typography>
      
      <div className="form-row">
        {/* <div className="form-field">
          <FormControlLabel
            control={
              <Switch
                checked={formData.IsActive ?? formData.isActive ?? true}
                onChange={(e) => handleInputChange('IsActive', e.target.checked)}
                disabled={modalAction === 'view'}
                color="success"
              />
            }
            label="Active"
          />
        </div> */}
        <div className="form-field">
          <FormControlLabel
            control={
              <Switch
                checked={formData.IsExpenseOrIncome ?? formData.isExpenseOrIncome ?? false}
                onChange={(e) => handleInputChange('IsExpenseOrIncome', e.target.checked)}
                disabled={modalAction === 'view'}
                color="primary"
              />
            }
            // label="Expense/Income"
             label="Income/Expense"
          />
        </div>
        <div className="form-field">
          <FormControlLabel
            control={
              <Switch
                checked={formData.IsTrading ?? formData.isTrading ?? false}
                onChange={(e) => handleInputChange('IsTrading', e.target.checked)}
                disabled={modalAction === 'view'}
                color="secondary"
              />
            }
            label="Trading"
          />
        </div>
      </div>

      {/* Opening Balances Section */}
      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1e3a8a', mb: 0.5, mt: 1, fontSize: '0.75rem' }}>
        Opening Balances (Branch-wise)
      </Typography>
      <Box sx={{ mb: 0.75 }}>
        {branchesLoading ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
            <CircularProgress size={14} />
            <span style={{ fontSize: '0.72rem', color: '#64748b' }}>Loading branches...</span>
          </Box>
        ) : (
          <Box sx={{ border: '1px solid #e5e7eb', borderRadius: '6px', overflow: 'hidden' }}>
            <Table size="small" sx={{ tableLayout: 'fixed' }}>
              <TableHead>
                <TableRow sx={{ background: '#f1f5f9' }}>
                  <TableCell sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#1e3a8a', py: 0.5, px: 1, width: '28%' }}>Branch</TableCell>
                  <TableCell sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#1e3a8a', py: 0.5, px: 1, width: '28%' }}>Opening Balance (₹)</TableCell>
                  <TableCell sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#1e3a8a', py: 0.5, px: 1, width: '26%' }}>Opening Date</TableCell>
                  <TableCell sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#1e3a8a', py: 0.5, px: 1, width: '18%', textAlign: 'center' }}>Payable</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(formData.OpeningBalances || []).map((row, idx) => (
                  <TableRow key={row.BranchId} sx={{ background: idx % 2 === 0 ? '#fff' : '#f8f9fa' }}>
                    <TableCell sx={{ fontSize: '0.72rem', py: 0.4, px: 1, color: '#374151', fontWeight: 500 }}>
                      {row.BranchName}
                    </TableCell>
                    <TableCell sx={{ py: 0.4, px: 1 }}>
                      <TextField
                        size="small"
                        type="number"
                        value={row.OpeningBalance ?? 0}
                        onChange={(e) => handleOpeningBalanceChange(row.BranchId, 'OpeningBalance', parseFloat(e.target.value) || 0)}
                        disabled={modalAction === 'view'}
                        inputProps={{ min: 0, style: { padding: '3px 6px', fontSize: '0.72rem' } }}
                        sx={{ '& .MuiOutlinedInput-root': { minHeight: '26px' }, width: '100%' }}
                      />
                    </TableCell>
                    <TableCell sx={{ py: 0.4, px: 1 }}>
                      <TextField
                        size="small"
                        type="date"
                        value={row.OpeningDate?.split('T')[0] || ''}
                        onChange={(e) => handleOpeningBalanceChange(row.BranchId, 'OpeningDate', e.target.value)}
                        disabled={modalAction === 'view'}
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ style: { padding: '3px 6px', fontSize: '0.72rem' } }}
                        sx={{ '& .MuiOutlinedInput-root': { minHeight: '26px' }, width: '100%' }}
                      />
                    </TableCell>
                    <TableCell sx={{ py: 0.4, px: 1, textAlign: 'center' }}>
                      <Switch
                        size="small"
                        checked={row.IsPayableOrReceivable ?? false}
                        onChange={(e) => handleOpeningBalanceChange(row.BranchId, 'IsPayableOrReceivable', e.target.checked)}
                        disabled={modalAction === 'view'}
                      />
                    </TableCell>
                  </TableRow>
                ))}
                {(formData.OpeningBalances || []).length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} sx={{ fontSize: '0.72rem', color: '#9ca3af', textAlign: 'center', py: 1 }}>
                      No branches available
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Box>
        )}
      </Box>

      {/* Description Section */}
      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1e3a8a', mb: 0.5, mt: 1, fontSize: '0.75rem' }}>
        Additional Details
      </Typography>
      
      <div className="form-field">
        <TextField
          fullWidth
          label="Description"
          placeholder="Enter account description (optional, max 150 chars)"
          multiline
          rows={2}
          value={formData.Description || formData.description || ''}
          onChange={(e) => handleInputChange('Description', e.target.value)}
          disabled={modalAction === 'view'}
          inputProps={{ maxLength: 150 }}
          helperText={`${(formData.Description || formData.description || '').length}/150 characters`}
        />
      </div>
    </div>
  );

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') {
          onClose();
        }
      }}
      maxWidth="md"
      fullWidth
      disableEscapeKeyDown
      className="account-head-modal"
    >
      <DialogTitle sx={{ p: 0 }}>
        <div className="modal-title-container">
          <div className="modal-title-text">
            <h2>{getModalTitle().title}</h2>
          </div>
          <Button
            onClick={onClose}
            className="modal-close-btn"
          >
            <Close />
          </Button>
        </div>
      </DialogTitle>
      
      <DialogContent>
        {renderForm()}
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose} variant="outlined">
          Cancel
        </Button>
        {modalAction !== 'view' && !isDuplicate && (
          <Button onClick={handleSave} variant="contained" disabled={loading}>
            {modalAction === 'create' ? 'Add Account' : 'Update'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default AccountHeadModal;
